import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Smartphone, Mail, MailCheck, AlertCircle, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PushStatusOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');

  const token = () => localStorage.getItem('token');

  const reload = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/push-status/overview`, { headers: { Authorization: `Bearer ${token()}` } });
      setData(res.data);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = (list) => {
    if (selected.size === list.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(list.map(c => c.id)));
    }
  };

  const sendReminders = async () => {
    if (selected.size === 0) {
      toast.info('Bitte mindestens einen Kunden auswählen');
      return;
    }
    if (!window.confirm(`Install-Erinnerung an ${selected.size} Kunden senden?`)) return;
    try {
      setSending(true);
      const res = await axios.post(
        `${API}/admin/push-status/send-install-reminder`,
        { customer_ids: Array.from(selected) },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success(`✉️ ${res.data.sent} E-Mails gesendet, ${res.data.skipped} übersprungen`);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Versand');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500 p-4">Lädt...</p>;
  if (!data) return null;

  const { totals, without_app, with_app } = data;
  const q = query.trim().toLowerCase();
  const filtered = without_app.filter(c => {
    if (!q) return true;
    return (
      (c.first_name || '').toLowerCase().includes(q) ||
      (c.last_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" data-testid="push-status-overview">
      {/* KPI Bar */}
      <div className="grid grid-cols-4 gap-3">
        <div className="border rounded-lg p-3 bg-white">
          <p className="text-xs text-gray-500">Kunden total</p>
          <p className="text-2xl font-bold">{totals.total_customers}</p>
        </div>
        <div className="border rounded-lg p-3 bg-green-50 border-green-200">
          <p className="text-xs text-green-700 flex items-center gap-1"><Smartphone className="w-3 h-3" /> Mit App</p>
          <p className="text-2xl font-bold text-green-700">{totals.with_app}</p>
        </div>
        <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
          <p className="text-xs text-amber-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Ohne App</p>
          <p className="text-2xl font-bold text-amber-700">{totals.without_app}</p>
        </div>
        <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
          <p className="text-xs text-blue-700">Install-Rate</p>
          <p className="text-2xl font-bold text-blue-700">{totals.app_install_rate}%</p>
        </div>
      </div>

      {/* Action-Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Suche Name oder E-Mail…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="pl-9"
            data-testid="push-status-search"
          />
        </div>
        <Badge variant="outline">{selected.size} ausgewählt</Badge>
        <Button size="sm" variant="outline" onClick={() => toggleAll(filtered)} data-testid="push-status-select-all">
          {selected.size === filtered.length && filtered.length > 0 ? 'Keine auswählen' : 'Alle anzeigen'}
        </Button>
        <Button size="sm" onClick={sendReminders} disabled={sending || selected.size === 0} data-testid="send-install-reminder-btn">
          <Mail className="w-4 h-4 mr-1" />
          {sending ? 'Sendet…' : `Install-Erinnerung senden (${selected.size})`}
        </Button>
      </div>

      {/* Liste "Ohne App" */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-amber-50 border-b px-3 py-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertCircle className="w-4 h-4" /> Kunden ohne App-Installation ({filtered.length})
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500 p-4 text-center">🎉 Alle Kunden haben die App!</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600 sticky top-0">
                <tr>
                  <th className="p-2 text-left w-10"></th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">E-Mail</th>
                  <th className="p-2 text-left">Telefon</th>
                  <th className="p-2 text-right">Ausgaben CHF</th>
                  <th className="p-2 text-left">Angemeldet</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const checked = selected.has(c.id);
                  const hasEmail = !!c.email;
                  return (
                    <tr key={c.id} className={`border-t hover:bg-amber-50/50 ${checked ? 'bg-amber-100/50' : ''}`} data-testid={`push-status-row-${c.id}`}>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!hasEmail}
                          onChange={() => toggleOne(c.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="p-2 font-medium">{c.first_name} {c.last_name}</td>
                      <td className="p-2 text-gray-600">
                        {hasEmail ? c.email : <span className="text-red-500 text-xs">Keine E-Mail</span>}
                      </td>
                      <td className="p-2 text-gray-500">{c.phone || '—'}</td>
                      <td className="p-2 text-right text-gray-600">{(c.total_spent || 0).toFixed(2)}</td>
                      <td className="p-2 text-gray-500 text-xs">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('de-CH') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Kunden mit App (kollapsiert) */}
      <details className="border rounded-lg">
        <summary className="bg-green-50 border-b px-3 py-2 flex items-center gap-2 text-sm font-semibold text-green-900 cursor-pointer">
          <MailCheck className="w-4 h-4" /> Kunden MIT App ({with_app.length}) — klicken zum Einblenden
        </summary>
        <div className="max-h-[320px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 sticky top-0">
              <tr>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">E-Mail</th>
                <th className="p-2 text-left">Gerät</th>
                <th className="p-2 text-left">Token registriert</th>
              </tr>
            </thead>
            <tbody>
              {with_app.map(c => (
                <tr key={c.id} className="border-t">
                  <td className="p-2">{c.first_name} {c.last_name}</td>
                  <td className="p-2 text-gray-600">{c.email}</td>
                  <td className="p-2"><Badge variant="outline" className="text-xs">{c.device_type || 'unknown'}</Badge></td>
                  <td className="p-2 text-gray-500 text-xs">
                    {c.fcm_updated_at ? new Date(c.fcm_updated_at).toLocaleString('de-CH') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};

export default PushStatusOverview;
